import React, { useState, useEffect } from "react";
import { useRedirect, useNotify, useGetList, useRefresh } from "react-admin";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  TextField,
  Chip,
  Stack
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import { isWarehouseManagerRole, getWarehouseFromRole } from "./roles";

// Custom component to display roles as chips
const RolesDisplay = ({ roles }) => {
  if (!roles) return null;
  
  // Ensure roles is always an array
  const rolesArray = Array.isArray(roles) ? roles : [roles];
  
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      {rolesArray.map((role, index) => {
        // Determine chip color based on role type
        let color = "default";
        let label = role;
        
        if (role === "admin") {
          color = "error";
          label = "Admin";
        } else if (isWarehouseManagerRole(role)) {
          color = "primary";
          const warehouse = getWarehouseFromRole(role);
          label = `${warehouse} Manager`;
        } else if (role.includes("manager")) {
          color = "secondary";
        }
        
        return (
          <Chip 
            key={index} 
            label={label} 
            color={color}
            size="small"
            sx={{ my: 0.5 }}
          />
        );
      })}
    </Stack>
  );
};

export const UserList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();
  
  // Get user data from the API
  const { data: users = [], isLoading, refetch } = useGetList("api/users", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'id', order: 'ASC' }
  });
  
  useEffect(() => {
    // Set loading state based on the API call
    setLoading(isLoading);
    
    // Clear error when data is loaded
    if (!isLoading && users.length > 0) {
      setError(null);
    }
  }, [isLoading, users]);
  
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    (user.username && user.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.firstName && user.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.lastName && user.lastName.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  // Get the current page of users
  const displayedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  // Handle edit user
  const handleEditUser = (id) => {
    redirect('edit', 'api/users', id);
  };
  
  // Handle add user
  const handleAddUser = () => {
    redirect('create', 'api/users');
  };
  
  // Handle refresh
  const handleRefresh = () => {
    setLoading(true);
    refetch()
      .then(() => {
        notify('User list refreshed', { type: 'info' });
        setLoading(false);
      })
      .catch(error => {
        setError('Failed to refresh user list');
        setLoading(false);
      });
  };
  
  if (loading && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 2,
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Users</Typography>
          
          <Tooltip title="Refresh data">
            <IconButton 
              onClick={handleRefresh} 
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddUser}
            color="primary"
          >
            Add User
          </Button>
        </Box>
      </Box>
      
      <TextField
        fullWidth
        label="Search by username or name"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(0);
        }}
        sx={{ mb: 2 }}
      />
      
      {error && (
        <Box sx={{ color: 'error.main', p: 2, mb: 2 }}>
          <Typography>{error}</Typography>
        </Box>
      )}
      
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Username</strong></TableCell>
                  <TableCell><strong>First Name</strong></TableCell>
                  <TableCell><strong>Last Name</strong></TableCell>
                  <TableCell><strong>Roles</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.firstName}</TableCell>
                    <TableCell>{user.lastName}</TableCell>
                    <TableCell>
                      <RolesDisplay roles={user.roles} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit user">
                        <IconButton 
                          onClick={() => handleEditUser(user.id)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {(filteredUsers.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
          />
        </Paper>
      )}
    </Box>
  );
};
